import React from "react";
import {
	baseStage,
	baseAPIUrl,
	AWS_UPLOADS_BUCKET,
	baseUploadsUrl,
	baseLibraryUrl,
	UTC_OFFSET,
	hasAvailableNews,
	newsHaveTags,
	DEFAULT_ALLOW_COMMENTS,
	MAKE_API_KEY,
	logo,
	clientName,
} from "../config";
import {
	COLOUR_BRANDING_APP,
	COLOUR_BRANDING_MAIN,
	COLOUR_BRANDING_OFF,
} from "../js/Colours";
import { PlussCore } from "./core";
// import * as PlussMaintenanceWebDev from '../pluss-extension-web/pluss-maintenance/src';
// import * as PlussNewsletterWebDev from '../pluss-extension-web/pluss-newsletter/src';
import * as PlussGroupsWeb from "@plusscommunities/pluss-circles-web-groups";
import * as PlussMaintenanceWeb from "@plusscommunities/pluss-maintenance-web";
import * as PlussFormsWeb from "@plusscommunities/pluss-maintenance-web-forms";
import * as PlussNewsletterWeb from "@plusscommunities/pluss-newsletter-web";
import * as PlussFeatureBuilderWeb from "@plusscommunities/pluss-feature-builder-web";
import * as PlussFeatureBuilderWebA from "@plusscommunities/pluss-feature-builder-web-a";
import * as PlussFeatureBuilderWebB from "@plusscommunities/pluss-feature-builder-web-b";

// List of extensions
const extensions = [
	PlussGroupsWeb,
	PlussMaintenanceWeb,
	PlussNewsletterWeb,
	PlussFormsWeb,
	PlussFeatureBuilderWeb,
	PlussFeatureBuilderWebA,
	PlussFeatureBuilderWebB,
];

const extensionViewWidgets = {};
const extensionViewFulls = {};
const extensionPreviewWidgets = {};
const extensionPreviewFulls = {};
const extensionPreviewGridIcons = {};
const extensionActivityTexts = {};
const extensionWidgetInfos = {};
const extensionWidgetOptions = [];
const extensionWidgetKeys = [];
const extensionFeatureInfo = [];
const extensionRoutes = [];
const extensionPermissions = [];
const extensionHiddenSections = [];
const extensionMoreSections = [];
const extensionAliases = [];
const extensionReducers = { ...PlussCore.Reducers };
const extensionScreens = {};
const extensionAnalytics = {};
const extensionDashboardLoaders = {};
const extensionStartupActions = [];
const extensionNotificationPreferences = [];

// Configure environment settings for the core
const environment = {
	baseStage,
	baseAPIUrl,
	baseUploadsUrl,
	baseLibraryUrl,
	uploadBucket: AWS_UPLOADS_BUCKET,
	colourBrandingMain: COLOUR_BRANDING_MAIN,
	colourBrandingOff: COLOUR_BRANDING_OFF,
	colourBrandingApp: COLOUR_BRANDING_APP,
	defaultProfileImage:
		"https://pluss-prd-uploads.s3.ap-southeast-2.amazonaws.com/uploads/users/ap-southeast-2:80aecdcb-9955-493e-a341-2f2263f64777/public/d6a654304734aca4f7ebb19249/profilepic.png",
	utcOffset: UTC_OFFSET,
	hasAvailableNews,
	newsHaveTags,
	defaultAllowComments: DEFAULT_ALLOW_COMMENTS,
	makeApiKey: MAKE_API_KEY,
	logo,
	clientName,
};
PlussCore.Config.init(environment);

/**
 * The internal structure used to register a feature with the PlussCore system.
 * @typedef {Object} ExtensionModule
 * @property {Object} Config - Configuration metadata.
 * @property {string} Config.key - Unique widget key.
 * @property {string} Config.singularName - Display name.
 * @property {string} [Config.description] - Description text.
 * @property {string} [Config.emptyText] - Empty state text.
 * @property {Array<Object>} [Config.widgetOptions] - Options for the settings panel.
 * @property {Object} [Config.menu] - Side menu configuration.
 * @property {string} Config.menu.Icon - Icon name.
 * @property {string} Config.menu.Label - Label text.
 * @property {React.ComponentType<any>} ViewWidget - React component for the dashboard widget view.
 * @property {React.ComponentType<any>} ViewFull - React component for the full page view.
 * @property {React.ComponentType<any>} [PreviewWidget] - React component for the feature picker preview.
 * @property {React.ComponentType<any>} [PreviewFull] - React component for the feature picker full preview.
 */
export const installExtension = (e) => {
	// Determine the key from the Config
	const {
		key,
		activities,
		description,
		emptyText,
		widgetOptions,
		menu,
		addUrl,
		addPermission,
		singularName,
		routes,
		permissions,
		hiddenSections,
		moreSections,
		aliases,
		hiddenFromFeaturePicker,
		featurePickerInfo,
		notificationPreference,
	} = e.Config;

	// Prevent duplicate registration
	if (extensionWidgetKeys.includes(key)) return;

	// Configure environment settings if the extension has an init
	if (e.Config.init) e.Config.init(environment);

	// Configure feature picker controls
	if (e.ViewWidget) extensionViewWidgets[key] = e.ViewWidget;
	if (e.ViewFull) extensionViewFulls[key] = e.ViewFull;
	if (e.PreviewWidget) extensionPreviewWidgets[key] = e.PreviewWidget;
	if (e.PreviewFull) extensionPreviewFulls[key] = e.PreviewFull;
	if (e.PreviewGrid) extensionPreviewGridIcons[key] = e.PreviewGrid;
	if (e.Analytics) extensionAnalytics[key] = e.Analytics;
	if (e.DashboardLoader) {
		extensionDashboardLoaders[key] = e.DashboardLoader;
	}

	// Configure activity list
	if (activities) {
		activities.forEach((a) => {
			extensionActivityTexts[a] = e.ActivityText;
		});
	}

	// Configure reducers
	if (e.Reducers) Object.assign(extensionReducers, e.Reducers);

	// Configure screens
	if (e.Screens) Object.assign(extensionScreens, e.Screens);

	// Configurations
	extensionWidgetInfos[key] = {
		description: description || "",
		emptyText: emptyText || "",
	};
	if (widgetOptions && widgetOptions.length > 0) {
		// Note: pushing to exported let/const arrays might require reassignment
		// but since arrays are reference types, pushing works if imported correctly.
		// ideally extensionWidgetOptions should be exported as a getter or mutable object
		widgetOptions.forEach((o) => {
			extensionWidgetOptions.push({ key, ...o });
		});
	}

	extensionWidgetKeys.push(key);

	// Handle both single menu object and array of menu objects
	const menuItems = Array.isArray(menu) ? menu : [menu].filter(Boolean);

	menuItems.forEach((menuItem) => {
		extensionFeatureInfo.push({
			key,
			addUrl: addUrl || "",
			addPermission: addPermission || "",
			singularName: singularName || "",
			hiddenFromFeaturePicker: hiddenFromFeaturePicker || false,
			featurePickerContent: e.FeaturePickerContent,
			featurePickerInfo,
			...menuItem,
		});
	});

	if (routes && routes.length > 0)
		routes.forEach((r) => extensionRoutes.push(r));
	if (permissions && permissions.length > 0) {
		permissions.forEach((p) => {
			extensionPermissions.push(p);
		});
	}
	if (hiddenSections && hiddenSections.length > 0)
		hiddenSections.forEach((h) => extensionHiddenSections.push(h));
	if (moreSections && moreSections.length > 0)
		moreSections.forEach((m) => extensionMoreSections.push(m));

	extensionAliases.push({ key, aliases: aliases || [] });

	// Collect startup actions
	if (e.getStartupAction) {
		extensionStartupActions.push(e.getStartupAction);
	}

	// Collect notification preferences for Personal Settings page
	// Extensions must provide: key, label, permission in their notificationPreference config
	if (notificationPreference && notificationPreference.permission) {
		extensionNotificationPreferences.push({
			...notificationPreference,
			feature: key,
		});
	}
};

// Install all the current extensions
extensions.forEach(installExtension);

const getExtensionViewWidget = (widget, renderDefault = () => {}) => {
	const ViewWidget = extensionViewWidgets[widget];
	return ViewWidget ? <ViewWidget key={widget} /> : renderDefault();
};

const getExtensionViewFull = (widget, page, renderDefault = () => {}) => {
	const ViewFull = extensionViewFulls[widget];
	return ViewFull ? <ViewFull key={widget} page={page} /> : renderDefault();
};

const getExtensionPreviewWidget = (
	widget,
	page,
	widgetTitle,
	backgroundColor,
	renderDefault = () => {},
) => {
	const PreviewWidget = extensionPreviewWidgets[widget];
	return PreviewWidget ? (
		<PreviewWidget
			key={widget}
			page={page}
			widgetTitle={widgetTitle}
			backgroundColor={backgroundColor}
			titleClassName={
				"featurePickerPreview_tile_title featurePickerPreview_tile_title-widget"
			}
		/>
	) : (
		renderDefault()
	);
};

const getExtensionPreviewFull = (
	widget,
	page,
	widgetTitle,
	backgroundColor,
	options,
	renderDefault = () => {},
) => {
	const PreviewFull = extensionPreviewFulls[widget];
	return PreviewFull ? (
		<PreviewFull
			key={widget}
			page={page}
			widgetTitle={widgetTitle}
			backgroundColor={backgroundColor}
			titleClassName={"featurePickerPreview_tile_title"}
			options={options}
		/>
	) : (
		renderDefault()
	);
};

const getExtensionPreviewGridIcon = (
	widget,
	colour,
	renderDefault = () => {},
) => {
	const PreviewGrid = extensionPreviewGridIcons[widget];
	return PreviewGrid ? (
		<PreviewGrid key={widget} colour={colour} />
	) : (
		renderDefault()
	);
};

const getActivityText = (
	data,
	classes,
	clickableClasses,
	highlightedClasses,
	renderDefault = () => {},
) => {
	const ActivityText = extensionActivityTexts[data.Type];
	return ActivityText ? (
		<ActivityText
			data={data}
			classes={classes}
			clickableClasses={clickableClasses}
			highlightedClasses={highlightedClasses}
		/>
	) : (
		renderDefault()
	);
};

export {
	PlussCore,
	extensionWidgetInfos,
	extensionWidgetOptions,
	extensionWidgetKeys,
	extensionFeatureInfo,
	extensionRoutes,
	extensionPermissions,
	extensionHiddenSections,
	extensionMoreSections,
	extensionAliases,
	extensionReducers,
	extensionScreens,
	extensionAnalytics,
	extensionDashboardLoaders,
	extensionStartupActions,
	extensionNotificationPreferences,
	getExtensionViewWidget,
	getExtensionViewFull,
	getExtensionPreviewWidget,
	getExtensionPreviewFull,
	getExtensionPreviewGridIcon,
	getActivityText,
};
